/**
 * Device context sent with every API request (usage tracking, Phase OBS stage 1).
 *
 * The API already sees the user-agent, but browsers FREEZE parts of it: Windows 11
 * still says "Windows NT 10.0", and macOS is stuck at "10_15_7". Chromium browsers
 * (Chrome, Edge, Opera) report the real values when asked, through
 * navigator.userAgentData. Safari and Firefox don't support it, and for them the
 * API falls back to its own user-agent parsing.
 *
 * Nothing here identifies the person: the device id is a random value stored in
 * this browser, used only to tell one browser from another (e.g. "the same login
 * on three different computers"). It is sent only when that value was actually
 * stored. A private window or a blocked-storage policy gets no id, so a reload
 * is not recorded as a new computer.
 */

const DEVICE_ID_KEY = "domx_device_id";
/** First requests wait this long for version and OS; later ones do not. */
const CONTEXT_WAIT_MS = 2000;

let os: string | undefined;
let clientVersion: string | undefined;
let deviceModel: string | undefined;
let contextReady: Promise<void> | null = null;
let contextSettled = false;
let stoppedWaiting = false;

const randomId = (): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = window.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * The stored id for this browser profile.
 * Re-read on each call so two tabs that create it together converge on one value.
 * Undefined when storage is blocked: do not invent an id that changes every load.
 */
const getDeviceId = (): string | undefined => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(DEVICE_ID_KEY, id);
      id = localStorage.getItem(DEVICE_ID_KEY) || id;
    }
    return id;
  } catch {
    return undefined;
  }
};

/** "Windows 11", "macOS 14.6", "Android 14", from Chromium's real platform data. */
const osLabel = (platform?: string, version?: string): string | undefined => {
  if (!platform) return undefined;
  const major = Number((version || "").split(".")[0]);
  if (platform === "Windows") {
    // Chromium reports Windows 11 as platformVersion 13.0.0 or higher.
    if (!version) return "Windows";
    return major >= 13 ? "Windows 11" : major > 0 ? "Windows 10" : "Windows";
  }
  if (!version) return platform;
  const shortVersion = version.split(".").slice(0, 2).join(".");
  return `${platform} ${shortVersion}`;
};

const loadClientVersion = (): Promise<void> =>
  fetch("/version.json", { cache: "no-cache" })
    .then((r) => {
      const type = r.headers.get("content-type") || "";
      // A missing file falls through to index.html (200). That is not a version.
      if (!r.ok || type.includes("text/html")) return null;
      return r.json();
    })
    .then((v) => {
      if (v && v.version) clientVersion = String(v.version);
    })
    .catch(() => undefined);

const loadOsAndModel = (): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uaData = (navigator as any).userAgentData;
  if (!uaData || typeof uaData.getHighEntropyValues !== "function") {
    return Promise.resolve();
  }
  return (
    uaData
      .getHighEntropyValues(["platformVersion", "model"])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((v: any) => {
        os = osLabel(v.platform || uaData.platform, v.platformVersion);
        deviceModel = v.model ? String(v.model) : undefined; // Android phones/tablets only
      })
      .catch(() => undefined)
  );
};

/** Call once at startup, before login, so version and OS are ready for the first request. */
export const initClientContext = (): void => {
  if (contextReady) return;
  contextReady = Promise.all([loadClientVersion(), loadOsAndModel()]).then(
    () => {
      contextSettled = true;
    },
    () => {
      contextSettled = true;
    }
  );
};

/** Resolves when version and OS are known, or after CONTEXT_WAIT_MS, once. */
const waitForClientContext = (): Promise<void> => {
  initClientContext();
  const pending = contextReady;
  if (contextSettled || stoppedWaiting || !pending) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stoppedWaiting = true;
      resolve();
    }, CONTEXT_WAIT_MS);
    pending.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      () => {
        clearTimeout(timer);
        resolve();
      }
    );
  });
};

/** The headers BaseApi adds to every request. Only what is known is sent. */
export const clientContextHeaders = async (): Promise<
  Record<string, string>
> => {
  await waitForClientContext();
  const headers: Record<string, string> = {
    "x-app-state": document.visibilityState === "hidden" ? "hidden" : "visible",
  };
  const deviceId = getDeviceId();
  if (deviceId) headers["x-device-id"] = deviceId;
  if (clientVersion) headers["x-client-version"] = clientVersion;
  if (os) headers["x-os"] = os;
  if (deviceModel) headers["x-device-model"] = deviceModel;
  if (window.screen && window.screen.width) {
    headers["x-screen"] = `${window.screen.width}x${window.screen.height}`;
  }
  return headers;
};
